import React, { useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { media } from '@atlaskit/primitives';

import { useSidebarContext } from '@components/learning/LearningSidebar/context/LearningSidebarContext';
import {
  getPopupPosition,
  POPUP_MASK_WIDTH,
  POPUP_LEFT_OFFSET,
} from '@components/learning/LearningSidebar/utils/getPosition';
import { CourseItem } from '@components/learning/LearningSidebar/types';

interface HoverPopupProps {
  courses: CourseItem[];
  renderPopup: (courseData: CourseItem) => React.ReactNode;
}

/**
 * Props for the HoverPopup component
 * @param {CourseCardProps[]} [courses] - Array of course data
 * @param {(courseData: CourseCardProps) => React.ReactNode} [renderPopup] - Function to render the popup
 */

export const HoverPopup: React.FC<HoverPopupProps> = ({ courses, renderPopup }) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const { hoverInfo, setHoverInfo } = useSidebarContext();

  const courseToShowInPopup = useMemo(
    () => courses.find(({ sys }) => sys.id === hoverInfo?.courseId)!,
    [hoverInfo?.courseId, courses],
  );

  const handlePopupLeave = (e: React.MouseEvent<HTMLDivElement>): void => {
    const popupId = popupRef.current?.children[0].id;

    const isMovedToOutside = (e.target as HTMLElement).closest(`#${popupId}`);

    if (isMovedToOutside) {
      setHoverInfo(null);
    }
  };

  if (!hoverInfo) {
    return null;
  }

  return (
    <div
      data-testid="sidebar-hover-popup"
      ref={popupRef}
      onMouseEnter={(): void => {
        setHoverInfo(hoverInfo);
      }}
      onMouseLeave={handlePopupLeave}
      css={[popupStyles, css(getPopupPosition(hoverInfo.courseCardPosition))]}
    >
      {renderPopup(courseToShowInPopup)}
    </div>
  );
};

const popupStyles = css({
  position: 'absolute',
  zIndex: 100,
  transform: 'translateY(-50%)',
  paddingLeft: `${POPUP_MASK_WIDTH + POPUP_LEFT_OFFSET}px`,

  [media.below.sm]: {
    display: 'none',
  },
});
